import { LogicDeps } from '@chegg-wt/core/state'
import { Config } from '@chegg-wt/core/config'
import { AppState, FSACreatorPayload } from '@chegg-wt/core/state/types'
import { GetFormattedCitationsV2 } from '@chegg-wt/core/oneGraphQueries/__generated__/GetFormattedCitationsV2'
import { createLogic } from 'redux-logic'
import { pipe } from 'rxjs'
import { addFields, addProjectId } from '@chegg-wt/core/utils/projects'
import { mapFormattedCitationsV2toCitation } from '@chegg-wt/core/queries/utils'
import { ErrorMsgs } from '@chegg-wt/core/constants/errorMsgs'
import sourceTypes from '@chegg-wt/core/constants/sourceTypes'
import { aliasToStyleId } from '@chegg-wt/core/utils/styles'
import { SESSION_STORAGE_KEYS } from '@chegg-wt/core/utils/sessionStorage'
import { UserStyles } from '@chegg-wt/core/utils/userStyles'
import { getCitationsFromSessionStorage } from '@chegg-wt/core/utils/writingForms'
import {
  getUpgradeCitationsFromSessionStorage,
  removeCitationFromSessionStorage,
  removeUpgradeCitationFromSessionStorage,
} from '@chegg-wt/core/utils/upgrade'
import {
  DELETE_MODAL,
  GATED_BIBLIOGRAPHY_ON_CITATION_COUNT_MODAL,
} from '../modals/constants'
import { navigated } from '../app/actions'
import { citationLoaded } from '../citation/actions'
import { sourceTypeSelected } from '../createCitation/actions'
import {
  createProject,
  fetchCurrentProject,
  softGateStyle,
  styleSelected,
} from '../currentProject/actions'
import { CITATIONS_PER_PAGE } from '../../constants/projects'
import { openModal, closeModal } from '../modals/actions'
import {
  currentStyleSelector,
  currentProjectIdSelector,
  shouldShowStyleGatedModalSelector,
} from '../currentProject/selectors'
import { isProSelector, userRoleSelector } from '../user/selectors'
import { AuthRedirectActions } from '../user/types'
import { writingFormSubmit } from '../form/actions'
import { currentCitationsPageSelector } from './selectors'
import {
  deleteCitations,
  deleteCitationsFailed,
  fetchCitations,
  fetchCitationsFailed,
  fetchCitationsSucceeded,
  editCitation,
  editCitationError,
  createNewCitation,
} from './actions'
import { GatedBibliographyOnCitationCountModalData } from './types'
import { ActionTypes } from './constants'

export const editCitationLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof editCitation>,
  any,
  LogicDeps
>({
  type: [editCitation],
  validate({ getState, action }, allow, reject) {
    const state = getState()

    const currentStyle = currentStyleSelector(state)
    const userRole = userRoleSelector(state)
    if (Config.enableStyleGate) {
      // check if gated style rule applies
      const userStyles = new UserStyles(userRole)
      // check if current style is gated
      const isStyleGated = !userStyles.isStyleAllowed(currentStyle)

      // if gated style, reject the request by the gated type
      if (isStyleGated && userStyles.isModalGate()) {
        return reject(softGateStyle(currentStyle))
      }
    }
    return allow(action)
  },
  async process({ action, api, getState, routes }, dispatch, done) {
    const { id: citationId } = action.payload.citation
    const projectId = currentProjectIdSelector(getState())

    // TODO: Show Loading/Progress Indicators
    // TODO: Collect Metrics

    try {
      const citation = await api.bff.getCitation({ projectId, citationId })
      const { value: sourceType } = sourceTypes.find(
        (el) => el.apiAlias === citation.source
      )
      const style = currentStyleSelector(getState())
      dispatch(sourceTypeSelected(sourceType))
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.searchData,
        JSON.stringify({ data: citation })
      )

      dispatch(citationLoaded(citation, null))
      dispatch(navigated(routes.toEditCitation({ citationId, style, sourceType })))
      // TODO: Hide Loading/Progress Indicators
    } catch (error) {
      dispatch(editCitationError(new Error(ErrorMsgs.EDIT_CITATION_ERROR)))
      // TODO: Hide Loading/Progress Indicators
    } finally {
      done()
    }
  },
})

export const deleteCitationsLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof deleteCitations>,
  any,
  LogicDeps
>({
  type: ActionTypes.DELETE_CITATIONS,
  async process({ api, action, getState }, dispatch, done) {
    const { citations } = action.payload
    const currentProjectId = currentProjectIdSelector(getState())
    try {
      await Promise.all(
        citations.map(({ id: citationId, projectId }) =>
          api.bff.deleteCitation({ citationId, projectId })
        )
      )
      dispatch(fetchCitations(currentProjectId))
      dispatch(closeModal(DELETE_MODAL))
    } catch (e) {
      dispatch(deleteCitationsFailed(e))
    } finally {
      done()
    }
  },
})

export const fetchCitationsLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof fetchCitations>,
  any,
  LogicDeps
>({
  type: ActionTypes.FETCH_CITATIONS,
  validate({ getState, action }, allow, reject) {
    const state = getState()
    const style = currentStyleSelector(state)
    const currentProjectId = currentProjectIdSelector(state)
    const { projectId } = action.payload

    // reject if style is null/undefined
    if (!style) {
      reject(fetchCitationsFailed(new Error('style is undefined/null')))
    }

    // if falsey ids, need to create project
    if (!currentProjectId && !projectId) {
      allow(createProject())
    } else if (currentProjectId && currentProjectId === projectId) {
      allow(action)
    } else {
      // fallback to currentProjectId if projectId is undefined
      reject(fetchCurrentProject({ projectId: projectId || currentProjectId }))
    }
  },
  async process({ action, api, getState }, dispatch, done) {
    try {
      const state = getState()
      const style = currentStyleSelector(state)
      const page = currentCitationsPageSelector(state)
      const { projectId } = action.payload
      const isProUser = isProSelector(state)
      const upgradeCitationObject = getUpgradeCitationsFromSessionStorage()
      const citationObject = getCitationsFromSessionStorage()

      if (isProUser && (upgradeCitationObject || citationObject)) {
        const { citation, initialCitationData, route } =
          upgradeCitationObject ?? citationObject
        const { action: authRedirectAction, style: sessionStyle, isPaymentDone } = route

        if (projectId && isPaymentDone) {
          if (authRedirectAction === AuthRedirectActions.CREATE_CITATION) {
            // create citation
            dispatch(
              writingFormSubmit({ citation, initialCitationData, redirect: false })
            )

            // delete session object
            if (upgradeCitationObject) removeUpgradeCitationFromSessionStorage()
            else removeCitationFromSessionStorage()

            // change style
            if (sessionStyle && style !== sessionStyle) {
              dispatch(styleSelected(sessionStyle, false, true, false))
              return done()
            }
          }
        }
      }

      const formattedCitationV2Data: GetFormattedCitationsV2 = (
        await api.oneGraph.getCitationsV2({
          citationProjectId: projectId,
          citationStyle: aliasToStyleId(style),
          limit: CITATIONS_PER_PAGE,
          page,
        })
      ).data
      const formattedCitations = mapFormattedCitationsV2toCitation(
        formattedCitationV2Data,
        style,
        projectId
      )

      const enhancedCitations = pipe(
        addFields,
        addProjectId(projectId)
      )(formattedCitations)

      dispatch(fetchCitationsSucceeded(enhancedCitations))
    } catch (e) {
      const message = e instanceof Error ? e.message : e
      dispatch(fetchCitationsFailed(new Error(message)))
      if (message.match('unknown citation style')) {
        // Project style is in a bad state set it to the default style
        dispatch(styleSelected(Config.currentStyle, false, true))
      }
    } finally {
      done()
    }
  },
})

export const createNewCitationLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof createNewCitation>,
  any,
  LogicDeps
>({
  type: ActionTypes.CREATE_NEW_CITATION,
  process({ routes, action, getState }, dispatch, done) {
    const { shouldWarn, shouldBlock, blockThreshold } = action.payload
    const state = getState()
    const currentStyle = currentStyleSelector(state)
    const shouldShowStyleGatedModal = shouldShowStyleGatedModalSelector(state)

    if (shouldShowStyleGatedModal) {
      dispatch(softGateStyle(currentStyle))
    } else if (shouldBlock || shouldWarn) {
      // check if bibliography is gated
      dispatch(
        openModal({
          name: GATED_BIBLIOGRAPHY_ON_CITATION_COUNT_MODAL,
          data: {
            blockThreshold,
            isWarningOnly: shouldWarn && !shouldBlock,
            currentStyle,
          } as GatedBibliographyOnCitationCountModalData,
        })
      )
    } else {
      dispatch(navigated(routes.toSourceTypeSelection({ style: currentStyle })))
    }
    done()
  },
})
